import { DEFAULT_THEME, THEME_ENUM } from '@/constants';
import { DEFAULT_DECIMALS } from '@/constants/global';
import { LOCAL_THEME } from '@/constants/storage';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedQuote } from '@/entities/WrappedQuote';
import { BigNumber } from 'ethers';
import _ from 'lodash';
import { formatUnits } from 'viem';

/**
 * polling func
 * @param {Function} func
 * @param {Number} interval
 */
export const pollingFunc = (func: () => void, interval = 3000, immediately = true): NodeJS.Timeout => {
  if (immediately) {
    func();
  }
  return setInterval(() => {
    func();
  }, interval);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export function bigNumberObjectCheck(obj: any, bigNumberKeys?: string[]): any {
  const cloneObj = _.clone(obj);
  if (cloneObj) {
    if (cloneObj) {
      try {
        Object.keys(cloneObj).forEach((key) => {
          const val = cloneObj[key];

          //reset bignumber if number is not correct class
          if (val?.bn && val?.value) {
            cloneObj[key] = BigNumber.from(val.value);
          } else if (val?._hex && BigNumber.isBigNumber(val) && !(val instanceof BigNumber)) {
            cloneObj[key] = BigNumber.from(val);
          } else if (val?.hex && val?.type === 'BigNumber') {
            cloneObj[key] = BigNumber.from(val);
          }
          // If bigNumberKeys is provided, only convert keys that are in the array
          else if (bigNumberKeys?.includes(key)) {
            if (typeof val === 'string') {
              cloneObj[key] = BigNumber.from(val || 0);
            } else {
              if (val.bn) {
                cloneObj[key] = BigNumber.from(val.value || 0);
              }
            }
          }
          // else if (typeof val === 'string' && /^\d+$/.test(val)) {
          //   // Parse numeric strings (including BigInt strings) to BigNumber
          //   cloneObj[key] = BigNumber.from(BigInt(val));
          // }
          else if (typeof val === 'object' && val && Object.keys(val).length > 0) {
            if (val instanceof WrappedBigNumber || val instanceof WrappedQuote) {
              cloneObj[key] = val;
            } else {
              const newVal = bigNumberObjectCheck(val, bigNumberKeys);
              cloneObj[key] = newVal;
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    }
  }
  return cloneObj;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function bigNumberObjectCheckByKeys(obj: any, bigNumberKeys?: string[]): any {
  const cloneObj = _.clone(obj);
  if (cloneObj) {
    if (cloneObj) {
      try {
        Object.keys(cloneObj).forEach((key) => {
          const val = cloneObj[key];

          // If bigNumberKeys is provided, only convert keys that are in the array
          if (bigNumberKeys?.includes(key)) {
            if (typeof val === 'string') {
              cloneObj[key] = BigNumber.from(val || 0);
            } else {
              if (val.bn) {
                cloneObj[key] = BigNumber.from(val.value || 0);
              }
            }
          } else if (typeof val === 'object' && val && Object.keys(val).length > 0) {
            if (val instanceof WrappedBigNumber || val instanceof WrappedQuote) {
              cloneObj[key] = val;
            } else {
              const newVal = bigNumberObjectCheckByKeys(val, bigNumberKeys);
              cloneObj[key] = newVal;
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    }
  }
  return cloneObj;
}
/**
 * Safely converts BigInt values to Numbers for specified keys, preventing precision loss
 * @param obj - The object to process
 * @param bigInitKeys - Array of keys that should be converted from BigInt to Number
 * @returns Processed object with safe BigInt to Number conversions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function bigIntToNumberCheckByKeys(obj: any, bigInitKeys?: string[]): any {
  const cloneObj = _.clone(obj);
  if (cloneObj) {
    if (cloneObj) {
      try {
        Object.keys(cloneObj).forEach((key) => {
          const val = cloneObj[key];
          // If bigInitKeys is provided, only convert keys that are in the array
          if (bigInitKeys?.includes(key)) {
            try {
              const bigIntVal = formatUnits(BigInt(val || 0), DEFAULT_DECIMALS);
              cloneObj[key] = Number(bigIntVal);
            } catch (conversionError) {
              console.error(`Failed to convert value for key "${key}" to BigInt:`, val, conversionError);
              // Keep original value if conversion fails
              cloneObj[key] = val;
            }
          } else if (typeof val === 'object' && val && Object.keys(val).length > 0) {
            const newVal = bigIntToNumberCheckByKeys(val, bigInitKeys);
            cloneObj[key] = newVal;
          }
        });
      } catch (error) {
        console.error('Error in bigIntToNumberCheckByKeys:', error);
      }
    }
  }
  return cloneObj;
}

export function getDefaultTheme(): THEME_ENUM {
  const localTheme = localStorage.getItem(LOCAL_THEME);
  if (localTheme) {
    return localTheme as THEME_ENUM;
  }
  return DEFAULT_THEME;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounceRequest<T extends (...args: any[]) => Promise<any>>(
  func: T,
  wait = 300,
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    return new Promise((resolve, reject) => {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        func(...args)
          .then(resolve)
          .catch(reject);
      }, wait);
    });
  };
}
